import axios from 'axios';
import { refreshTokensSingleFlight } from './tokenRefresh';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors (race fix 2026-07-08):
// - NEVER wipe storage / hard-redirect on a bare 401 — during boot the token
//   may simply be mid-refresh; the old handler nuked the session on a race.
// - Instead: single-flight refresh (shared with AuthContext so the single-use
//   refresh token is never double-consumed) and retry the request ONCE.
// - Only a DEFINITIVE server rejection of the refresh token clears storage
//   and sends the user to /login.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original: any = error.config || {};
    const url = String(original.url || '');
    const isAuthCall = url.includes('/auth/login') || url.includes('/auth/refresh');
    if (error.response?.status === 401 && !original._authRetry && !isAuthCall) {
      original._authRetry = true;
      const result = await refreshTokensSingleFlight();
      if (result.ok && result.accessToken) {
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${result.accessToken}` };
        return api(original);
      }
      if (result.definitive) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      // Transient refresh failure (network/5xx): keep tokens, surface error.
    }
    return Promise.reject(error);
  }
);

// Types
export interface KanbanTicket {
  return_trip_required?: boolean;
  return_of_ticket_id?: number | null;
  return_ticket_id?: number | null;
  visit_count?: number;
  missed?: boolean;
  needs_return?: boolean;
  aged?: boolean;
  aged_days?: number | null;
  id: number;
  ticket_number: string;
  quickbooks_work_order?: string;
  title: string;
  description?: string;
  priority: string;
  status: string;
  ticket_type?: string;
  customer_id?: number;
  customer_name?: string;
  contact_name?: string;
  contact_phone?: string;
  assigned_to_id?: number;
  assigned_to_name?: string;
  assigned_to_initials?: string;
  call_received_at?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  service_address_line1?: string;
  service_city?: string;
  service_state?: string;
  created_at: string;
  time_since_created?: string;
  elapsed_time_minutes?: number;
}

export interface KanbanBoard {
  unassigned: KanbanTicket[];
  assigned: KanbanTicket[];
  en_route: KanbanTicket[];
  on_site: KanbanTicket[];
  in_progress: KanbanTicket[];
  pending_parts: KanbanTicket[];
  completed_today: KanbanTicket[];
}

export interface TicketEditHistory {
  id: number;
  field_name: string;
  old_value?: string;
  new_value?: string;
  changed_by?: {
    id: number;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  created_at: string;
}

export interface Customer {
  id: number;
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
}

export interface Technician {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  initials: string;
  assigned_tickets: number;
  is_active: boolean;
}

export interface SystemSetting {
  id: number;
  key: string;
  value?: string;
  value_type: string;
  description?: string;
  updated_at: string;
}

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
};

// Tickets API
export const ticketsApi = {
  visitOutcome: (id: number, outcome: string, notes?: string) =>
    api.post(`/tickets/${id}/visit-outcome`, { outcome, notes }),
  getKanbanBoard: () => api.get<KanbanBoard>('/tickets/kanban'),
  list: (params?: any) => api.get('/tickets', { params }),
  get: (id: number) => api.get(`/tickets/${id}`),
  create: (data: any) => api.post('/tickets', data),
  update: (id: number, data: any) => api.patch(`/tickets/${id}`, data),
  delete: (id: number) => api.delete(`/tickets/${id}`),
  assign: (id: number, data: { assigned_to_id: number; scheduled_start?: string; scheduled_end?: string; notes?: string }) =>
    api.post(`/tickets/${id}/assign`, data),
  unassign: (id: number) => api.post(`/tickets/${id}/unassign`),
  changeStatus: (id: number, status: string, notes?: string, latitude?: number, longitude?: number) =>
    api.post(`/tickets/${id}/status`, { status, notes, latitude, longitude }),
  getHistory: (id: number) => api.get<TicketEditHistory[]>(`/tickets/${id}/history`),
  // Phase 7d portal action: approve | deny | question | edit
  portalAction: (
    id: number,
    body: { action: 'approve' | 'deny' | 'question' | 'edit'; changes?: any; comment?: string; silent?: boolean },
  ) => api.post(`/tickets/${id}/action`, body),
  // Close a return-flagged ticket and open a linked follow-up return call
  closeCreateReturn: (
    id: number,
    body: { reason?: string; close_status?: string; scheduled_start?: string; assigned_to_id?: number; copy_notes?: boolean },
  ) => api.post(`/tickets/${id}/close-create-return`, body),
  // change 16 (2026-11-01, P2-E): dispatcher approves that the tech may close and bill the ticket
  approveClose: (id: number, body: { note?: string; revoke?: boolean } = {}) =>
    api.post(`/tickets/${id}/approve-close`, body),
  // Bid-origin installation orders: manager signs off on the overage amount
  overageSignoff: (id: number, body: { amount: number; note?: string }) =>
    api.post(`/tickets/${id}/overage-signoff`, body),
  // install order (from the bid sheet): parts classified/received -> enters the ticket process (pending review)
  partsCleared: (id: number, note: string = '') =>
    api.post(`/tickets/${id}/parts-cleared`, { note }),
};

// Customers API
export const customersApi = {
  list: (search?: string) => api.get<Customer[]>('/customers', { params: { search } }),
  get: (id: number) => api.get<Customer>(`/customers/${id}`),
  create: (data: any) => api.post<Customer>('/customers', data),
  update: (id: number, data: any) => api.patch<Customer>(`/customers/${id}`, data),
};

// Technicians API
export const techniciansApi = {
  list: () => api.get<Technician[]>('/technicians'),
  getTickets: (id: number) => api.get<KanbanTicket[]>(`/technicians/${id}/tickets`),
};

// Settings API
export const settingsApi = {
  getAll: () => api.get<SystemSetting[]>('/settings'),
  get: (key: string) => api.get<SystemSetting>(`/settings/${key}`),
  update: (key: string, value: string) => api.put<SystemSetting>(`/settings/${key}`, { value }),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getAlerts: () => api.get('/dashboard/alerts'),
  getRecentTickets: () => api.get('/dashboard/recent-tickets'),
};

// Users API (for admin)
export const usersApi = {
  list: () => api.get('/users'),
  get: (id: number) => api.get(`/users/${id}`),
  create: (data: any) => api.post('/users', data),
  update: (id: number, data: any) => api.patch(`/users/${id}`, data),
  delete: (id: number) => api.delete(`/users/${id}`),
};

// Audit API
export const auditApi = {
  list: (params?: any) => api.get('/audit', { params }),
};


// Tracking API (Employee Tracker Integration)
export const trackingApi = {
  getDevices: () => api.get('/tracking/devices'),
  assignDevice: (deviceId: string, userId: number) =>
    api.post(`/tracking/devices/${deviceId}/assign`, null, { params: { user_id: userId } }),
  unassignDevice: (deviceId: string) =>
    api.post(`/tracking/devices/${deviceId}/unassign`),
  getLiveLocations: () => api.get('/tracking/locations/live'),
  getGpsSettings: () => api.get('/tracking/gps-settings'),
  getMapSettings: () => api.get('/tracking/map-settings'),
  updateMapSettings: (data: any) => api.patch('/tracking/map-settings', data),
  updateGpsSettings: (data: any) => api.patch('/tracking/gps-settings', data),
  getLocationHistory: (userId: number, params?: { date?: string; start?: string; end?: string }) =>
    api.get(`/tracking/locations/history/${userId}`, { params }),
};

// ============================================================================
// Device <-> Employee management API  (v2.3.0)
// Pairing codes, binding, retire (history retained), purge, per-device config.
// ============================================================================
export const devicesApi = {
  list: (includeRetired = true) => api.get('/devices', { params: { include_retired: includeRetired } }),
  employees: () => api.get('/devices/employees'),
  issueCode: (userId: number, ttlMinutes = 15) =>
    api.post('/devices/pairing-codes', { user_id: userId, ttl_minutes: ttlMinutes }),
  listCodes: () => api.get('/devices/pairing-codes'),
  revokeCode: (code: string) => api.delete(`/devices/pairing-codes/${code}`),
  assign: (deviceId: string, userId: number) =>
    api.post(`/devices/${deviceId}/assign`, null, { params: { user_id: userId } }),
  unassign: (deviceId: string) => api.post(`/devices/${deviceId}/unassign`),
  retire: (deviceId: string) => api.post(`/devices/${deviceId}/retire`),
  reactivate: (deviceId: string) => api.post(`/devices/${deviceId}/reactivate`),
  purge: (deviceId: string) =>
    api.delete(`/devices/${deviceId}/purge`, { params: { confirm: deviceId } }),
  getConfig: (deviceId: string) => api.get(`/devices/${deviceId}/config`),
  putConfig: (deviceId: string, data: any) => api.put(`/devices/${deviceId}/config`, data),
  repairIdentity: () => api.post('/devices/repair-identity'),
  // v2.4.0 device console
  status: () => api.get('/devices/status'),
  telemetry: (deviceId: string, hours = 24) =>
    api.get(`/devices/${deviceId}/telemetry`, { params: { hours } }),
  apps: (deviceId: string) => api.get(`/devices/${deviceId}/apps`),
  appsRollup: () => api.get('/devices/apps/rollup'),
  adaCapture: (deviceId: string) => api.get(`/devices/${deviceId}/ada-capture`),   // v3.0.25 ADA screen capture
};

// Ticket attachments (job photos + customer signature from the tracker app)
export const attachmentsApi = {
  list: (ticketId: number) => api.get(`/tickets/${ticketId}/attachments`),
  upload: (ticketId: number, file: File, description?: string, source?: 'tech' | 'office' | 'customer') => {
    const fd = new FormData();
    fd.append('file', file);
    if (description) fd.append('description', description);
    if (source) fd.append('source', source);   // Files tab (2026-09-04)
    return api.post(`/tickets/${ticketId}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  remove: (attachmentId: number) => api.delete(`/attachments/${attachmentId}`),
  fileUrl: (attachmentId: number) => `${api.defaults.baseURL}/attachments/${attachmentId}/file`,
  files: (ticketId: number) => api.get(`/tickets/${ticketId}/files`),   // grouped: emails / pictures{tech,office,customer} / files
};

// Shared tech@customer.concept.bz mailbox (2026-09-04): short-lived auto-login link
export const techMailApi = {
  link: (mailbox?: string) => api.get('/tech-mail/link', { params: mailbox ? { mailbox } : {} }),   // any mailbox the user's roles allow (2026-09-04)
  accounts: () => api.get('/tech-mail/accounts'),
  status: (mailbox?: string) => api.get('/tech-mail/status', { params: mailbox ? { mailbox } : {} }),
};

// Multi-role workspace (2026-09-04): active role, authorized functions, role mailboxes
export const workspaceApi = {
  me: () => api.get('/workspace/me'),
  setActiveRole: (role_id: number | null) => api.put('/workspace/active-role', { role_id }),
  roleMailboxes: () => api.get('/workspace/role-mailboxes'),
  saveRoleMailboxes: (items: { role_id: number; mailbox: string; label?: string | null }[]) => api.put('/workspace/role-mailboxes', { items }),
};

// Geocoding API (Server-side)
export const geocodeApi = {
  validate: (street: string, city: string, state: string, postalCode?: string) =>
    api.post('/geocode/validate', null, { params: { street, city, state, postal_code: postalCode } }),
  geocodeTicket: (ticketId: number) =>
    api.post(`/geocode/ticket/${ticketId}`),
  batch: (addresses: Array<{ street?: string; city?: string; state?: string; postal_code?: string }>) =>
    api.post('/geocode/batch', addresses),
};

// Map API
export const mapApi = {
  getData: (includeCompleted: boolean = false) =>
    api.get('/map/data', { params: { include_completed: includeCompleted } }),
};

// Tracker device types
export interface TrackerDevice {
  device_id: string;
  device_name: string;
  employee_id: string | null;
  employee_name: string | null;
  platform: string;
  status: string;
  last_seen: string | null;
  app_version: string | null;
  assigned_to: { user_id: number; user_name: string; tracking_enabled: boolean } | null;
}

export interface TechnicianLive {
  id: number;
  name: string;
  initials: string;
  email: string;
  phone: string | null;
  color: string;
  status: string;
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  speed: number | null;
  last_location_update: string | null;
  device_id: string;
  current_job_id: number | null;
  current_job_title: string | null;
  current_job_address: string | null;
  jobs_today: number;
  jobs_completed_today: number;
  time_state?: string;
  time_ticket_id?: number | null;
  time_since?: string | null;
  heading?: number | null;
  speed_mph?: number | null;
  altitude?: number | null;
  altitude_ft?: number | null;
  battery_level?: number | null;
  battery_state?: string | null;
  activity_state?: string | null;
  steps_today?: number | null;
}

export interface LocationPoint {
  lat: number;
  lng: number;
  accuracy: number | null;
  altitude: number | null;
  speed: number | null;
  heading: number | null;
  timestamp: string;
}

export interface LocationHistory {
  user_id: number;
  user_name: string;
  device_id: string;
  date: string;
  points: LocationPoint[];
  clock_events: Array<{ type: string; timestamp: string }>;
  total_points: number;
  filtered_points?: number;
  flight_segments?: any[];
  displayed_points: number;
}


// Weather & Hazard API
export const weatherApi = {
  getCurrent: (lat: number, lon: number) =>
    api.get('/weather/current', { params: { lat, lon } }),
  getAlerts: (bbox?: string, lat?: number, lon?: number) =>
    api.get('/weather/alerts', { params: { bbox, lat, lon } }),
  getTileUrls: () => api.get('/weather/tiles'),
  getRadar: () => api.get('/weather/radar'),
};

export default api;

// ── Schedule types ──────────────────────────────────────────────────────

export interface ScheduleStop {
  stop_type: 'job' | 'lunch' | 'end_of_day';
  ticket_id: number | null;
  title: string;
  address: string;
  lat: number | null;
  lng: number | null;
  estimated_duration_minutes: number;
  travel_from_previous_seconds: number;
  travel_from_previous_miles: number;
  travel_from_previous_polyline: [number, number][];
  depart_at: string | null;
  arrive_at: string | null;
  finish_at: string | null;
  notes: string;
}

export interface DailySchedule {
  tech_id: number;
  tech_name: string;
  schedule_date: string;
  day_start: string;
  day_end: string;
  lunch_after_stop: number;
  lunch_time: string | null;
  lunch_duration_minutes: number;
  end_destination: string;
  end_destination_address: string;
  start_lat: number | null;
  start_lng: number | null;
  start_address: string;
  stops: ScheduleStop[];
  total_drive_miles: number;
  total_drive_minutes: number;
  total_work_minutes: number;
  warnings: string[];
}

export interface BuildScheduleRequest {
  tech_id: number;
  schedule_date: string;
  ticket_ids?: number[];
  day_start?: string;
  day_end?: string;
  lunch_after_stop?: number;
  lunch_time?: string | null;
  lunch_duration_minutes?: number;
  end_destination?: string;
  end_destination_lat?: number;
  end_destination_lng?: number;
  end_destination_address?: string;
  start_lat?: number;
  start_lng?: number;
  include_polylines?: boolean;
}

// Schedule API
export const scheduleApi = {
  build: (req: BuildScheduleRequest) =>
    api.post<DailySchedule>('/schedule/build', req),
  optimizeOrder: (data: { tech_id: number; schedule_date: string; ticket_ids: number[]; start_lat: number; start_lng: number }) =>
    api.post<{ optimized_ticket_ids: number[] }>('/schedule/optimize-order', data),
  recalculate: (data: { tech_id: number; schedule_date: string; from_stop_index?: number; current_lat?: number; current_lng?: number }) =>
    api.post<DailySchedule>('/schedule/recalculate', data),
  getDaily: (techId: number, date: string) =>
    api.get<DailySchedule>(`/schedule/daily/${techId}/${date}`),
  travelEstimate: (fromLat: number, fromLng: number, toLat: number, toLng: number) =>
    api.get<{ duration_seconds: number; duration_minutes: number; distance_miles: number }>('/schedule/travel-estimate', {
      params: { from_lat: fromLat, from_lng: fromLng, to_lat: toLat, to_lng: toLng }
    }),
};

export const partsApi = {
  search: (q: string) => api.get('/parts/search', { params: { q, limit: 20 } }),
  categories: () => api.get('/parts/categories'),
  list: (params?: any) => api.get('/parts', { params }),
  get: (id: number) => api.get(`/parts/${id}`),
  create: (data: any) => api.post('/parts', data),
  update: (id: number, data: any) => api.patch(`/parts/${id}`, data),
};

// Inventory (ERP-synced warehouses + transfers)
export const inventoryApi = {
  summary: () => api.get('/inventory/summary'),
  warehouses: () => api.get('/inventory/warehouses'),
  partStock: (partNumber: string) => api.get(`/inventory/stock/${encodeURIComponent(partNumber)}`),
  transfers: (status?: string) => api.get('/inventory/transfers', { params: status ? { status } : {} }),
  createTransfer: (data: { part_number: string; qty: number; from_warehouse: string; to_warehouse?: string; note?: string }) =>
    api.post('/inventory/transfers', data),
  actOnTransfer: (id: number, action: string, note?: string) =>
    api.post(`/inventory/transfers/${id}/action`, { action, note }),
  moveStock: (data: { part_number: string; qty: number; from_warehouse: string; to_warehouse: string; note?: string }) =>
    api.post('/inventory/move', data),
};

export const lineItemsApi = {
  list: (ticketId: number) => api.get(`/tickets/${ticketId}/line-items`),
  add: (ticketId: number, data: any) => api.post(`/tickets/${ticketId}/line-items`, data),
  update: (ticketId: number, itemId: number, data: any) => api.patch(`/tickets/${ticketId}/line-items/${itemId}`, data),
  delete: (ticketId: number, itemId: number) => api.delete(`/tickets/${ticketId}/line-items/${itemId}`),
};

export const timeTrackingApi = {
  getTicketSummary: (ticketId: number) => api.get(`/time-tracking/tickets/${ticketId}/summary`),
  getTicketEntries: (ticketId: number) => api.get(`/time-tracking/tickets/${ticketId}/entries`),
  getTechState: (techId: number) => api.get(`/time-tracking/tech/${techId}/state`),
  getAllStates: () => api.get('/time-tracking/tech/states'),
  getSettings: () => api.get('/time-tracking/settings'),
  updateSettings: (data: any) => api.patch('/time-tracking/settings', data),
  createEntry: (data: any) => api.post('/time-tracking/entries', data),
  updateEntry: (id: number, data: any) => api.patch(`/time-tracking/entries/${id}`, data),
  deleteEntry: (id: number) => api.delete(`/time-tracking/entries/${id}`),
};

// ── Meal / rest break compliance (California labor-law) ──
export interface BreakState {
  tech_id: number;
  break_type: 'meal1' | 'meal2';
  status: 'pending' | 'due' | 'taken' | 'exception' | 'violation';
  shift_start: string | null;
  eligible_at: string | null;
  due_by: string | null;
  minutes_until_due: number | null;
  taken_at: string | null;
  message: string;
}
export interface TechBreakStatus {
  tech_id: number;
  tech_name: string;
  breaks: BreakState[];
  action_required: boolean;
}
export const mealBreaksApi = {
  getSettings: () => api.get('/meal-breaks/settings'),
  getStatus: (techId: number) => api.get<TechBreakStatus>(`/meal-breaks/status/${techId}`),
  getAllStatus: () => api.get<{ as_of: string; techs: TechBreakStatus[] }>('/meal-breaks/status'),
  take: (techId: number, break_type?: string) =>
    api.post(`/meal-breaks/${techId}/take`, { break_type }),
  grantException: (techId: number, break_type?: string, reason?: string) =>
    api.post(`/meal-breaks/${techId}/exception`, { break_type, reason }),
  scan: () => api.post('/meal-breaks/scan'),
};


// ── v2.3.4 VEHICLES / FUEL / STOP CLASSIFICATION ───────────────────────────
// Every write below is ADMIN-GATED ON THE SERVER (403 for tech/dispatcher).
export const vehiclesApi = {
  mine: () => api.get('/vehicles'),
  all: () => api.get('/vehicles/admin/all'),
  get: (id: number) => api.get(`/vehicles/${id}`),
  create: (data: any) => api.post('/vehicles', data),
  update: (id: number, data: any) => api.patch(`/vehicles/${id}`, data),
  assign: (id: number, userIds: number[]) => api.post(`/vehicles/${id}/assign`, { user_ids: userIds }),
  unassign: (id: number, userId: number) => api.post(`/vehicles/${id}/unassign`, { user_id: userId }),
  deactivate: (id: number) => api.post(`/vehicles/${id}/deactivate`),
  reactivate: (id: number) => api.post(`/vehicles/${id}/reactivate`),
  setCurrent: (id: number) => api.post(`/vehicles/${id}/set-current`),
};

export const fuelApi = {
  list: (params?: { vehicle_id?: number; user_id?: number; limit?: number }) =>
    api.get('/fuel-logs', { params }),
  create: (data: any) => api.post('/fuel-logs', data),
};

export const stopsApi = {
  settings: () => api.get('/tracking/stop-settings'),
  match: (latitude: number, longitude: number) =>
    api.get('/tracking/stop-match', { params: { latitude, longitude } }),
  classify: (data: any) => api.post('/tracking/stops/classify', data),
  classifications: (techId: number, date: string) =>
    api.get(`/tracking/stops/${techId}/${date}/classifications`),
};


// ── Ticket ops: context (franchise/corp/owner + contacts), timeline,
// ── cancellation, and the two-level pricing reveal.
export const ticketOpsApi = {
  context: (ticketId: number) => api.get(`/tickets/${ticketId}/context`),
  timeline: (ticketId: number) => api.get(`/tickets/${ticketId}/timeline`),
  cancel: (ticketId: number, data: { reason: string; reason_code?: string | null; customer_requested?: boolean }) => // change 16 (2026-11-01, P2-E): customer_requested -> late-cancellation fee rule
    api.post(`/tickets/${ticketId}/cancel`, data),
  closeNoCharge: (ticketId: number, data: { reason?: string | null }) =>
    api.post(`/tickets/${ticketId}/close-no-charge`, data),
  // LEVEL 1 — the right to reveal at all (admin grants it)
  grantOnTicket: (ticketId: number, granted: boolean, note?: string) =>
    api.post(`/tickets/${ticketId}/pricing-grant`, { granted, note }),
  grantOnProfile: (userId: number, granted: boolean, note?: string) =>
    api.post(`/users/${userId}/pricing-grant`, { granted, note }),
  // LEVEL 2 — the tech approves visibility, per item or all at once
  revealLineItem: (ticketId: number, itemId: number, revealed: boolean) =>
    api.post(`/tickets/${ticketId}/line-items/${itemId}/reveal-pricing`, { revealed }),
  revealAll: (ticketId: number, revealed: boolean) =>
    api.post(`/tickets/${ticketId}/reveal-all-pricing`, { revealed }),
  // The two documents. Opposite pricing rules — never merge these.
  fieldSignoff: (ticketId: number) => api.get(`/tickets/${ticketId}/field-signoff`),
  finalInvoice: (ticketId: number) => api.get(`/tickets/${ticketId}/final-invoice`),
  contacts: (customerId: number) => api.get(`/customers/${customerId}/contacts`),
  addContact: (customerId: number, data: any) => api.post(`/customers/${customerId}/contacts`, data),
  setHierarchy: (customerId: number, data: any) => api.patch(`/customers/${customerId}/hierarchy`, data),
};

// ── Compliance review (post-hoc; never a gate) ──
export const complianceApi = {
  summary: () => api.get('/compliance/summary'),
  reviews: (params?: any) => api.get('/compliance/reviews', { params }),
  review: (id: number, data: { review_status: string; reviewer_notes?: string | null }) =>
    api.post(`/compliance/reviews/${id}/review`, data),
  actions: () => api.get('/compliance/actions'),
  toggleAction: (key: string, enabled: boolean) => api.put(`/compliance/actions/${key}`, { enabled }),
  reviewers: () => api.get('/compliance/reviewers'),
  addReviewer: (data: { user_id: number; action_key?: string | null }) => api.post('/compliance/reviewers', data),
  revokeReviewer: (id: number) => api.delete(`/compliance/reviewers/${id}`),
};
